import { createSelector } from '@reduxjs/toolkit';
import { getAuthToken } from '@/redux/modules/user';
import { getDeployment } from '@/redux/modules/config';
import { GlobalState } from '../rootReducer';
import api from '@/redux/api/sellerCatalogs';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import ms from 'ms';
import union from 'lodash/union';

export const LOAD_SELLER_PREVIEW_CATALOGS_FAIL = 'la/domain/sellerPreviewCatalogs/LOAD_FAIL';
export const LOAD_SELLER_PREVIEW_CATALOGS_REQUEST = 'la/domain/sellerPreviewCatalogs/LOAD_REQUEST';
export const LOAD_SELLER_PREVIEW_CATALOGS_SUCCESS = 'la/domain/sellerPreviewCatalogs/LOAD_SUCCESS';

const REDUX_STORE_TIME = ms('30m');

export const DEFAULT_STATE = {
    byCatalogId: {},
    byId: {},
    loaded: {},
    loading: [],
};

export type State = typeof DEFAULT_STATE;

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_SELLER_PREVIEW_CATALOGS_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.sellerId]),
            };
        case LOAD_SELLER_PREVIEW_CATALOGS_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
            };
        case LOAD_SELLER_PREVIEW_CATALOGS_SUCCESS:
            const { sellerId } = action.meta;
            const existing = cloneDeep(state.byCatalogId);
            action.payload.catalogPreviews.forEach((preview) => {
                existing[preview.previewId] = preview;
            });
            return {
                ...state,
                byCatalogId: existing,
                byId: {
                    ...state.byId,
                    [sellerId]: [...action.payload.catalogPreviews].sort((a, b) => {
                        return a.saleStartTs - b.saleStartTs;
                    }),
                },
                loaded: { ...state.loaded, [sellerId]: action.meta.actionTime },
                loading: difference(state.loading, [sellerId]),
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.sellerPreviewCatalogs;
const idSelector = (state: GlobalState, id: number) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byId);
const byCatalogIdSelector = createSelector(stateSelector, (state) => state.byCatalogId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

// takes in a (state, id) and returns the data
export const getSellerPreviewCatalogs = createSelector([byIdSelector, idSelector], (byId, id) => byId[id] || []);
export const getSellerPreviewCatalogIds = createSelector(
    getSellerPreviewCatalogs,
    (previewCatalogs) => (previewCatalogs || []).map((prev) => prev.previewId) || []
);

export const getCatalogPreview = createSelector(
    [byCatalogIdSelector, idSelector],
    (byCatalogId, id) => byCatalogId[id] || {}
);

// takes in a (state, id) and returns the load time
export const getLoadTimeForSellerPreviewCatalogs = createSelector(
    [loadedSelector, idSelector],
    (loaded, id) => loaded[id] || 0
);

// takes in a (state, id) and returns the is loading
export const isSellerPreviewCatalogsLoading = createSelector([loadingSelector, idSelector], (loading, id) => {
    return loading.includes(id);
});

const shouldFetchPreviewCatalogs = (state, sellerId) => {
    if (!sellerId) {
        return false;
    }
    const item = getSellerPreviewCatalogs(state, sellerId);
    if (item.length) {
        const loaded = getLoadTimeForSellerPreviewCatalogs(state, sellerId);
        const time = Date.now();
        const diff = time - loaded;
        if (diff < REDUX_STORE_TIME) {
            return false;
        }
    }
    const loading = isSellerPreviewCatalogsLoading(state, sellerId);
    return !loading;
};

/* ACTION CREATORS */
const loadPreviewCatalogs = (sellerId) => async (dispatch, getState) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            payload: sellerId,
            type: LOAD_SELLER_PREVIEW_CATALOGS_REQUEST,
        });
        const response = await api.fetchSellerPreviewCatalogsById({
            authToken,
            deployment,
            sellerId,
        });
        dispatch({
            meta: { actionTime: Date.now(), sellerId },
            payload: response.data,
            type: LOAD_SELLER_PREVIEW_CATALOGS_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            meta: { sellerId },
            payload: error,
            type: LOAD_SELLER_PREVIEW_CATALOGS_FAIL,
        });
    }
};

export const fetchSellerPreviewCatalogsIfNeeded =
    (sellerId: number) => async (dispatch: Function, getState: Function) => {
        if (shouldFetchPreviewCatalogs(getState(), sellerId)) {
            return dispatch(loadPreviewCatalogs(sellerId));
        }
        return Promise.resolve();
    };
